import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { carregarSimulador, type DadosSimulador } from '../api/simulador'
import { nomePlataforma } from '../api/trafegoPago'
import { useFiltros } from '../state/FiltrosContext'
import { formatarInteiro, formatarReais } from '../utils/formato'
import { DIAS_MES, dividir, projetar, verbaAtual, type Modelo } from '../utils/simulador'

const br = (iso: string) => iso.split('-').reverse().join('/')
const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
/** Estimativa não tem casa decimal: seria precisão que ela não tem. */
const aprox = (v: number) => formatarInteiro(Math.round(v))
const custo = (inv: number, conv: number) => (conv > 0 ? formatarReais(inv / conv) : '—')

type Totais = { verba: number; conversoes: number; baixa: number; alta: number; valor: number }
function somar(modelos: Modelo[], verbas: Record<string, number>): Totais {
  return modelos.reduce<Totais>(
    (t, m) => {
      const v = verbas[m.campanha_id] ?? 0
      const p = projetar(m, v)
      return { verba: t.verba + v, conversoes: t.conversoes + p.conversoes, baixa: t.baixa + p.baixa, alta: t.alta + p.alta, valor: t.valor + p.valor }
    },
    { verba: 0, conversoes: 0, baixa: 0, alta: 0, valor: 0 },
  )
}

function Resumo({ titulo, hoje, cenario, faixa }: { titulo: string; hoje: string; cenario: string; faixa?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 200 }}>
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Typography variant="h6" sx={{ ...num, fontWeight: 700, lineHeight: 1.3 }}>
        {cenario}
      </Typography>
      {faixa ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={num}>
          {faixa}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" component="div" sx={num}>
        hoje: {hoje}
      </Typography>
    </Paper>
  )
}

/**
 * Simulador de orçamento: mexer na verba diária de cada campanha (ou num total, com a divisão
 * sugerida) e ver as conversões estimadas por mês. Só estima — para mudar a verba nas contas, o
 * caminho é o "Aplicar" das sugestões, com os limites dele.
 */
export function SimuladorPage() {
  const { marcaId } = useFiltros()
  const [dados, setDados] = useState<DadosSimulador | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [verbas, setVerbas] = useState<Record<string, number>>({})
  const [total, setTotal] = useState('')
  const [sobra, setSobra] = useState(0)

  useEffect(() => {
    if (!marcaId) return
    let cancelado = false
    setDados(null)
    setErro(null)
    carregarSimulador(marcaId)
      .then((d) => {
        if (cancelado) return
        setDados(d)
        setVerbas(Object.fromEntries(d.modelos.map((m) => [m.campanha_id, verbaAtual(m)])))
        setSobra(0)
      })
      .catch((e: Error) => !cancelado && setErro(e.message))
    return () => {
      cancelado = true
    }
  }, [marcaId])

  const modelos = useMemo(() => dados?.modelos ?? [], [dados])
  const atuais = useMemo(() => Object.fromEntries(modelos.map((m) => [m.campanha_id, verbaAtual(m)])), [modelos])
  const hoje = useMemo(() => somar(modelos, atuais), [modelos, atuais])
  const cenario = useMemo(() => somar(modelos, verbas), [modelos, verbas])
  const temValor = hoje.valor > 0
  const nomeConv = dados?.conversao ? dados.conversao.toLowerCase() : 'conversões'

  const sugerir = () => {
    const t = Number(total.replace(',', '.'))
    if (!(t > 0)) return
    const d = dividir(modelos, t)
    setVerbas(d.verbas)
    setSobra(d.sobra)
  }
  const voltar = () => {
    setVerbas(atuais)
    setSobra(0)
  }

  const cabecalho = (
    <Box sx={{ maxWidth: 820 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
          Simulador de orçamento
        </Typography>
        <Chip size="small" color="warning" variant="outlined" label="Estimativa" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Mude a verba diária das campanhas e veja quantas {nomeConv} dá para esperar por mês. A estimativa sai do histórico de cada campanha nos
        últimos 90 dias: como as conversões responderam quando a verba subiu ou desceu. Nada muda nas contas por aqui.
      </Typography>
    </Box>
  )

  if (!marcaId) {
    return (
      <Stack spacing={2}>
        {cabecalho}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">Escolha uma marca no topo: cada marca mede conversão de um jeito, então o simulador é por marca.</Typography>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      {cabecalho}
      {erro ? <Alert severity="error">{erro}</Alert> : null}
      {!dados && !erro ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {dados && !modelos.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">Nenhuma campanha de {dados.marca} gastou nos últimos 28 dias: não há o que simular.</Typography>
        </Paper>
      ) : null}

      {dados && modelos.length ? (
        <>
          {dados.demo ? <Alert severity="warning">Esta marca tem dados de demonstração: a simulação usa números fictícios.</Alert> : null}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <Resumo titulo="Verba por mês" hoje={formatarReais(hoje.verba * DIAS_MES)} cenario={formatarReais(cenario.verba * DIAS_MES)} />
            <Resumo
              titulo={`${dados.conversao ?? 'Conversões'} por mês (estimadas)`}
              hoje={`~${aprox(hoje.conversoes * DIAS_MES)}`}
              cenario={`~${aprox(cenario.conversoes * DIAS_MES)}`}
              faixa={`entre ${aprox(cenario.baixa * DIAS_MES)} e ${aprox(cenario.alta * DIAS_MES)}`}
            />
            <Resumo
              titulo="Custo por conversão (estimado)"
              hoje={custo(hoje.verba, hoje.conversoes)}
              cenario={custo(cenario.verba, cenario.conversoes)}
              faixa={cenario.alta > 0 ? `entre ${custo(cenario.verba, cenario.alta)} e ${custo(cenario.verba, cenario.baixa)}` : undefined}
            />
            {temValor ? (
              <Resumo titulo="Receita por mês (estimada)" hoje={formatarReais(hoje.valor * DIAS_MES)} cenario={formatarReais(cenario.valor * DIAS_MES)} />
            ) : null}
          </Stack>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} useFlexGap flexWrap="wrap">
              <TextField
                size="small"
                label="Verba total por dia"
                value={total}
                onChange={(e) => setTotal(e.target.value.replace(/[^\d,.]/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && sugerir()}
                placeholder={String(Math.round(hoje.verba))}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> } }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
              <Button variant="contained" onClick={sugerir} disabled={!(Number(total.replace(',', '.')) > 0)} sx={{ textTransform: 'none' }}>
                Sugerir divisão
              </Button>
              <Button onClick={voltar} sx={{ textTransform: 'none' }}>
                Voltar à verba de hoje
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
                A divisão põe cada real onde a estimativa rende mais {nomeConv}, sem passar do que cada campanha já gastou. Campanhas pausadas e sem
                conversão ficam como estão.
              </Typography>
            </Stack>
            {sobra > 0.5 ? (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Sobraram {formatarReais(sobra)}/dia: as campanhas já estão no maior gasto do histórico delas, e acima disso não dá para estimar.
              </Alert>
            ) : null}
          </Paper>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campanha</TableCell>
                    <TableCell align="right">Hoje (por dia)</TableCell>
                    <TableCell align="right">No cenário (por dia)</TableCell>
                    <TableCell align="right">{dados.conversao ?? 'Conversões'} por mês</TableCell>
                    <TableCell align="right">Custo por conversão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modelos.map((m) => {
                    const v = verbas[m.campanha_id] ?? 0
                    const p = projetar(m, v)
                    const avisos: Array<{ texto: string; dica: string; cor: 'warning' | 'default' | 'info' }> = []
                    if (!m.elasticidade) avisos.push({ texto: 'Sem base', dica: 'Nenhuma conversão nos últimos 28 dias: não dá para estimar.', cor: 'default' })
                    else if (m.elasticidade.fonte === 'faixa_padrao')
                      avisos.push({ texto: 'Resposta não medida', dica: `Usa a faixa típica de anúncios porque ${m.elasticidade.motivo}. A faixa fica mais larga.`, cor: 'info' })
                    if (p.foraDoHistorico)
                      avisos.push({
                        texto: 'Fora do histórico',
                        dica: `A campanha gastou entre ${formatarReais(m.historico.minimo)} e ${formatarReais(m.historico.maximo)} por dia (média de 7 dias) nos últimos 90 dias. Longe disso a estimativa é pouco confiável.`,
                        cor: 'warning',
                      })
                    if (!m.ativa) avisos.push({ texto: 'Pausada', dica: 'Não gastou nos últimos 7 dias.', cor: 'default' })
                    return (
                      <TableRow key={m.campanha_id} hover>
                        <TableCell sx={{ maxWidth: 340 }}>
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {m.campanha}
                          </Typography>
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                              {nomePlataforma(m.plataforma)}
                            </Typography>
                            {avisos.map((a) => (
                              <Tooltip key={a.texto} title={a.dica}>
                                <Chip size="small" variant="outlined" color={a.cor} label={a.texto} sx={{ height: 20, fontSize: 11 }} />
                              </Tooltip>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={num}>
                          {formatarReais(verbaAtual(m))}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={Math.round(v)}
                            onChange={(e) => setVerbas((x) => ({ ...x, [m.campanha_id]: Math.max(0, Number(e.target.value) || 0) }))}
                            slotProps={{ htmlInput: { min: 0, step: 5, 'aria-label': `Verba por dia de ${m.campanha}`, style: { textAlign: 'right' } } }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={num}>
                          {m.elasticidade ? (
                            <>
                              ~{aprox(p.conversoes * DIAS_MES)}
                              <Typography variant="caption" color="text.secondary" component="div">
                                {aprox(p.baixa * DIAS_MES)} a {aprox(p.alta * DIAS_MES)}
                              </Typography>
                            </>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right" sx={num}>
                          {m.elasticidade ? custo(v, p.conversoes) : '—'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 900 }}>
            Como é calculado: o ritmo de hoje é a média por dia de cada campanha nos últimos 28 dias (até{' '}
            {br(dados.fim)}). A resposta à verba é medida no histórico de {br(dados.inicio)} a {br(dados.fim)}: em geral, cada real a mais rende um pouco
            menos que o anterior. A faixa junta a incerteza dessa medida e a do próprio ritmo (com poucas conversões, ele é pouco preciso). A verba
            também mudou por outros motivos no período (época do ano, criativo novo), que o cálculo não separa: use como ordem de grandeza, não como
            promessa. Mês = 30 dias.
          </Typography>
        </>
      ) : null}
    </Stack>
  )
}
